import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { BsSearch } from 'react-icons/bs';
import { FiArrowUpRight, FiX } from 'react-icons/fi';
import axios from 'axios';
import { API_URL } from '../../config/api';
import defaultAvatar from '../../images/default.png';

export default function SearchInput({ setSearchModal }) {
  const dialog = useRef(null);
  const searchField = useRef(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [state, setState] = useState('idle');
  const [attempt, setAttempt] = useState(0);
  const term = query.trim();

  useEffect(() => {
    const element = dialog.current;
    const previousOverflow = document.body.style.overflow;
    element.showModal();
    searchField.current?.focus();
    document.body.style.overflow = 'hidden';
    return () => { element.close(); document.body.style.overflow = previousOverflow; };
  }, []);

  useEffect(() => {
    if (!term) { setResults([]); setState('idle'); return; }
    const controller = new AbortController();
    setState('loading');
    const timer = setTimeout(() => {
      axios.get(`${API_URL}/api/v1/posts/search`, { params: { userName: term }, signal: controller.signal, timeout: 8000 })
        .then(({ data }) => {
          if (controller.signal.aborted) return;
          if (!Array.isArray(data)) throw new Error('Invalid search response');
          setResults(data); setState('ready');
        }).catch(() => { if (!controller.signal.aborted) setState('error'); });
    }, 300);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [term, attempt]);

  const close = () => setSearchModal(false);
  return createPortal(<dialog ref={dialog} className="people-search-dialog" aria-labelledby="people-search-title" onCancel={event => { event.preventDefault(); close(); }} onClick={event => { if (event.target === dialog.current) close(); }}>
    <div className="people-search-heading"><div><h2 id="people-search-title">Find your people</h2><p>Search for creators by username.</p></div><button type="button" className="people-search-close" aria-label="Close search" onClick={close}><FiX /></button></div>
    <label className="people-search-field"><BsSearch aria-hidden="true" /><input ref={searchField} type="search" aria-label="Search people and creators" placeholder="Enter a username…" value={query} onChange={event => setQuery(event.target.value)} autoComplete="off" spellCheck="false" /></label>
    <div className="people-search-results" aria-busy={state === 'loading'}>
      {!term && <div className="people-search-message"><BsSearch aria-hidden="true" /><p>A good laugh starts with good company.</p><span>Type a username to find someone new.</span></div>}
      {term && state === 'loading' && <p className="people-search-status" role="status">Searching for “{term}”…</p>}
      {term && state === 'error' && <div className="people-search-message" role="alert"><p>Search couldn’t load.</p><span>Please check your connection and try again.</span><button onClick={() => setAttempt(value => value + 1)}>Try again</button></div>}
      {term && state === 'ready' && results.length === 0 && <div className="people-search-message" role="status"><p>No people found for “{term}”.</p><span>Try a different name or check the spelling.</span></div>}
      {term && state === 'ready' && results.length > 0 && <><p className="people-search-count" role="status">{results.length} {results.length === 1 ? 'person' : 'people'} found</p><ul>{results.map(person => <li key={person.id}><Link className="people-search-result" to={`/account?ud=${encodeURIComponent(person.id)}`} onClick={close}>
        <img src={person.profile_picture_link && String(person.profile_picture_link) !== '1' ? person.profile_picture_link : defaultAvatar} alt="" width="42" height="42" onError={event => { event.currentTarget.onerror = null; event.currentTarget.src = defaultAvatar; }} />
        <span><strong>{person.username}</strong><small>View profile</small></span><FiArrowUpRight aria-hidden="true" />
      </Link></li>)}</ul></>}
    </div>
    <div className="people-search-footer"><span>Discover your next favorite creator</span><span><kbd>Esc</kbd> to close</span></div>
  </dialog>, document.body);
}
